/**
 * QR Reader: the rig's scanning satellite (registration v2 mockup frame 13).
 * QR scanning is NOT on the rig itself: the rig HOME leads with the capture
 * modes and reacts to scans done elsewhere. The QR Reader is a separate,
 * handheld-shaped surface (the greeter's signed-in phone, scoped to one job)
 * that identifies a person and sends them to the rig.
 *
 * Re-skin over the live logic: it reads the EXISTING useCaptureState (RTDB)
 * for the rig-busy interlock and arms through the EXISTING
 * ingest.arm({qrToken}), the same callable the console's arm-by-id uses. No
 * new capture logic.
 *
 * The camera (S7): "Tap to scan" (the user gesture iOS wants) opens the rear
 * camera with getUserMedia, plays it in the viewfinder and decodes a frame
 * about every 120 ms, with the browser's BarcodeDetector when it can read QR
 * codes, else jsqr on a canvas snapshot. A decode that parses as a personal
 * token (qrParse.js, the server's shape) stops the camera and opens the
 * confirm card: the token is hashed in the browser to find the entry in the
 * live roster (name, number, badges, masked email) and the greeter asks the
 * person their name before "Send to rig". A token the roster does not show
 * yet (registered seconds ago, or subscription lag) can still be sent: the
 * server resolves it. The camera stops on decode, on Close, on unmount and
 * when the rig goes busy. Every await in the flow (the permission prompt, the
 * token hash) comes back through the scan run (createScanRun): a stop, a
 * close, a busy rig, a rescan or a new start while it was out ends the run,
 * and the continuation then changes nothing, so a pass decoded before Stop
 * cannot put its card over the next scan, and a camera granted after Close
 * is stopped, not attached. Stop is on the screen while the permission prompt
 * is up as well: the prompt can sit there until someone answers it, and a
 * greeter who tapped by mistake takes the reader back with one tap, with the
 * next Tap to scan ready straight away.
 *
 * Group check-in (2026-09-28): while the rig is armed and Start has not been
 * pressed, the busy screen lists the group and offers "Add a person to this
 * capture". That puts the reader in add mode for THIS arm (its lease): the
 * camera works again, the confirm card asks to add the person to the first
 * person's capture, and the send is addGroupMember instead of arm. Add mode
 * ends after an add, on Back, and by itself the moment the rig moves on (Start,
 * cancel, another arm): the camera stops and the card is dropped, so a pass
 * scanned for a group can never arm someone on their own. addFor ({ jobId,
 * leaseId }, the console's "Scan a pass") opens the reader straight in add
 * mode for that exact arm, and only if the rig still shows it.
 *
 * Group fixes (2026-09-29): until the rig's live state has arrived, or after
 * its subscription failed, the reader treats the rig as busy and says why
 * (rigStateProblem): no Tap to scan, no Send. A Send the server refuses (a
 * phone whose view of the rig was old) shows the server's plain words; one
 * that gets no answer says the person may have been sent or added.
 */
import React, { useCallback, useEffect, useRef, useState } from "react";
import { useCaptureState } from "../../../../lib/useCaptureState.js";
import { useFirestoreCollection } from "../../../../lib/useFirestore.js";
import { ingest, getDeviceId, newLeaseId } from "../../../../lib/ingestCallables.js";
import { RosterBadges, maskEmail, addWithRetakeCheck, groupAddErrorText, isClearRefusal } from "./RigScreenV2.jsx";
import { parsePersonalQr, hashQrToken } from "./qrParse.js";
import { rigv2 } from "./rigv2Callables.js";
import {
  GroupList, addModeFor, firstName as groupFirstName, groupCountLabel, groupJoinProblem, groupJoinProblemText, groupOpen, groupPeopleOf, maxGroupSizeOf,
} from "./GroupCheckin.jsx";

const SAMPLE_MS = 120;      // one decode attempt about every 120 ms
const NOT_PASS_MS = 2500;   // the not-a-pass line stays this long after the last miss
const JSQR_MAX_SIDE = 640;  // jsqr reads a downscaled frame; a pass QR survives this

// The words on the reader: plain, the mockup's lines where it has them.
export const CAMERA_ERROR_TEXT = "Camera not available. Allow the camera for nycsuite.io, or type the number at the capture station.";
export const DECODER_ERROR_TEXT = "The QR decoder did not load. Reload the page, or type the number at the capture station.";
export const NOT_PASS_TEXT = "That is not a pass QR. Try again.";
export const NOT_IN_LIST_TEXT = "Not in the list yet. Send to rig anyway?";
const IDLE_LINE = "The rig is idle. Scan the next person when they are at the station.";
const STARTING_LINE = "Allow the camera when the phone asks, or tap Stop.";
const NOTE_LINE = "A phone camera screenshot of the pass, or the email, both scan. No pass? The operator can type the name or number at the capture station.";

const initials = (name) =>
  (name || "?").split(/\s+/).filter(Boolean).slice(0, 2).map((s) => s[0].toUpperCase()).join("") || "?";
const firstName = (name) => String(name ?? "").trim().split(/\s+/)[0] || "";

// jsqr ships a CommonJS build; depending on who bundled it the function is
// the module, its default, or its default's default.
function jsqrFrom(mod) {
  if (typeof mod === "function") return mod;
  if (typeof mod?.default === "function") return mod.default;
  if (typeof mod?.default?.default === "function") return mod.default.default;
  throw new Error("jsqr did not load");
}

// jsqr on a canvas snapshot of the current frame, downscaled to JSQR_MAX_SIDE.
async function makeJsqrDecoder() {
  const jsQR = jsqrFrom(await import("jsqr"));
  const canvas = document.createElement("canvas");
  const ctx = canvas.getContext("2d", { willReadFrequently: true });
  if (!ctx) throw new Error("no 2d canvas");
  return {
    native: false,
    decode: async (video) => {
      const vw = video.videoWidth;
      const vh = video.videoHeight;
      if (!vw || !vh) return null;
      const scale = Math.min(1, JSQR_MAX_SIDE / Math.max(vw, vh));
      const w = Math.max(1, Math.round(vw * scale));
      const h = Math.max(1, Math.round(vh * scale));
      if (canvas.width !== w || canvas.height !== h) { canvas.width = w; canvas.height = h; }
      ctx.drawImage(video, 0, 0, w, h);
      const img = ctx.getImageData(0, 0, w, h);
      const code = jsQR(img.data, w, h, { inversionAttempts: "dontInvert" });
      return code ? code.data : null;
    },
  };
}

// The browser's BarcodeDetector when it exists and can read QR codes (Chrome
// on Android; a desktop Chrome without a barcode backend reports no formats
// and lands on jsqr), else jsqr. Both take the <video> element and give back
// the decoded text, or null for a frame with no code in it.
async function makeDecoder() {
  const Detector = globalThis.BarcodeDetector;
  if (typeof Detector === "function") {
    try {
      const formats = typeof Detector.getSupportedFormats === "function" ? await Detector.getSupportedFormats() : ["qr_code"];
      if (formats.includes("qr_code")) {
        const detector = new Detector({ formats: ["qr_code"] });
        return {
          native: true,
          decode: async (video) => {
            const codes = await detector.detect(video);
            return codes.length ? String(codes[0].rawValue ?? "") : null;
          },
        };
      }
    } catch { /* fall through to jsqr */ }
  }
  return makeJsqrDecoder();
}

/**
 * The scan run: which start, camera run or decode the reader is on. A
 * continuation that comes back from an await (the permission prompt in
 * startCamera, the token hash after a decode, a decode-loop tick) holds the
 * id of the run it began in and asks isCurrent(id) afterwards; when the run
 * has moved on it ends with no state change, because whatever moved it (Stop,
 * Close, the rig going busy, a rescan, another start) already put the reader
 * where it belongs. begin() opens a new run and returns its id, so every
 * earlier id is stale from then on; invalidate() ends the current run with no
 * successor. Ids only grow, so a stale id never comes back. Plain JS, no
 * React: exported for the unit test.
 */
export function createScanRun() {
  let current = 0;
  return {
    begin() { current += 1; return current; },
    invalidate() { current += 1; },
    isCurrent(id) { return id === current; },
  };
}

/**
 * Group check-in: which scanned card may be shown and sent. A card scanned in
 * add mode (pending.forLease, the arm it was scanned for) only while the
 * reader is still adding to that same arm; a card scanned to arm only outside
 * add mode; else none. Pure: exported for the unit test.
 */
export function sendableCard(pending, adding, addLease) {
  if (!pending) return null;
  if (pending.forLease) return adding && pending.forLease === addLease ? pending : null;
  return adding ? null : pending;
}

/**
 * Whether the reader knows the rig's live state (group fixes, 2026-09-29).
 * Until the first value arrives (state null), or once the subscription has
 * failed (it stops for good then, and the state it last had may be stale),
 * the reader does not know whether the rig is free: it is treated as busy, so
 * there is no Tap to scan and no Send, and the screen says why. Before, both
 * read as idle, and a phone could send a guest to a rig that was capturing.
 * Returns null when the state is known, else { kind: "loading" } or
 * { kind: "error", message }. Pure: exported for the test.
 */
export function rigStateProblem(state, error) {
  if (error) return { kind: "error", message: error?.message ?? String(error) };
  if (state == null) return { kind: "loading" };
  return null;
}

/**
 * What a failed Send to rig says. A clear refusal (the rig is busy, the pass
 * is not on this job) in the server's own words; anything else (a timeout, a
 * dropped connection) may have sent them, so say that and what to do. Pure:
 * exported for the test.
 */
export function sendErrorText(err, name) {
  if (isClearRefusal(err)) return err?.message ?? String(err);
  const who = String(name ?? "").trim() || "This person";
  return `${who} may have been sent to the rig: no answer came back. If the rig does not show them in a moment, tap Send to rig again.`;
}

export default function QrReaderView({ jobId, job, sessionId = null, onClose, interactive = true, addFor = null, signedInAs = "", onSignOut = undefined }) {
  const { state, error: stateError } = useCaptureState(jobId);
  const stateProblem = rigStateProblem(state, stateError);
  const phase = state?.phase ?? "idle";
  // Group check-in: addLease is the arm this reader is adding people to. Add
  // mode holds only while that arm is still on the rig and still open.
  const [addLease, setAddLease] = useState(null);
  const open = !stateProblem && groupOpen(state);
  const adding = open && addLease !== null && addLease === state?.leaseId;
  // A rig whose state is not known counts as busy: no camera, no card, no Send.
  const rigBusy = stateProblem !== null || (phase !== "idle" && !adding);

  // The live roster (a subscription, the way the console reads it), so a pass
  // registered a moment ago resolves on the confirm card without a remount.
  const { data: roster, error: rosterError } = useFirestoreCollection(jobId ? `jobs/${jobId}/rosterEntries` : null);

  const [scanState, setScanState] = useState("idle"); // idle | scanning | identified
  const [starting, setStarting] = useState(false);     // getUserMedia in flight (the permission prompt)
  const [pending, setPending] = useState(null);        // { token, hash } once a pass decoded
  const [cameraError, setCameraError] = useState(null);
  const [notPass, setNotPass] = useState(false);
  const [history, setHistory] = useState([]);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState(null);
  const [removingId, setRemovingId] = useState(null); // group check-in: a remove out

  const videoRef = useRef(null);
  const streamRef = useRef(null);
  const timerRef = useRef(null);
  const scanRef = useRef(null);      // the scan run (createScanRun): stops, starts and decodes move it; a stale continuation ends
  if (scanRef.current === null) scanRef.current = createScanRun();
  const notPassRef = useRef({ shown: false, timer: null });
  const mountedRef = useRef(true);
  const rigBusyRef = useRef(rigBusy);
  rigBusyRef.current = rigBusy;
  // Group check-in: the lease a pass decoded now would be added to (null
  // outside add mode). Every scanned card carries it (pending.forLease).
  const addModeLeaseRef = useRef(null);
  addModeLeaseRef.current = adding ? addLease : null;

  // The card that may be shown and sent: one scanned in add mode only while
  // that same add mode holds, one scanned to arm only outside add mode. So a
  // pass scanned for a group is never sent to the rig on its own, even in the
  // moment between add mode ending and its card being dropped.
  const cardPending = sendableCard(pending, adding, addLease);

  // The entry behind the scanned pass, from the live roster. null while the
  // hash is unknown ("" without WebCrypto) or the entry has not reached this
  // phone yet; it fills in on its own when the subscription catches up.
  const identified = cardPending?.hash ? (roster.find((r) => r.qrTokenHash === cardPending.hash) ?? null) : null;

  // Stop the camera: the decode loop, its timer, every track, the video's
  // source, and the start that has not landed yet. Every path that ends a scan
  // run goes through here (Stop, Close, a busy rig, a rescan, a decode,
  // unmount), so each of them also puts starting back to false. A permission
  // prompt still out is then dropped when it answers, its stream stopped by
  // the stale-run check in startCamera, and the reader is back on Tap to scan
  // without waiting for the rig to go busy.
  const stopCamera = useCallback(() => {
    scanRef.current.invalidate();
    if (timerRef.current) { clearTimeout(timerRef.current); timerRef.current = null; }
    const stream = streamRef.current;
    streamRef.current = null;
    if (stream) for (const track of stream.getTracks()) track.stop();
    const video = videoRef.current;
    if (video) {
      try { video.pause(); } catch { /* not playing */ }
      video.srcObject = null;
    }
    const np = notPassRef.current;
    if (np.timer) clearTimeout(np.timer);
    notPassRef.current = { shown: false, timer: null };
    setNotPass(false);
    setStarting(false);
  }, []);

  // Open the rear camera. Called from a tap (iOS wants a user gesture): "Tap to
  // scan" or "Wrong person, scan again". A refusal, a phone without a camera or
  // plain http leaves the reader usable, with the camera line under the viewfinder.
  const startCamera = useCallback(async () => {
    if (rigBusyRef.current || !interactive || starting) return;
    setError(null); setCameraError(null);
    const media = globalThis.navigator?.mediaDevices;
    if (!media || typeof media.getUserMedia !== "function") { setCameraError(CAMERA_ERROR_TEXT); return; }
    setStarting(true);
    // This start's run. The permission prompt can stay up for a while; a Stop,
    // a Close, a busy rig or another start meanwhile ends the run, and what
    // the prompt then hands back is dropped here without touching the reader.
    const run = scanRef.current.begin();
    let stream;
    try {
      stream = await media.getUserMedia({ video: { facingMode: { ideal: "environment" } }, audio: false });
    } catch {
      if (!scanRef.current.isCurrent(run)) return;
      setStarting(false); setCameraError(CAMERA_ERROR_TEXT);
      return;
    }
    if (!scanRef.current.isCurrent(run)) {
      // The scan moved on while the prompt was up: this stream is not wanted.
      for (const track of stream.getTracks()) track.stop();
      return;
    }
    if (rigBusyRef.current) {
      // The rig went busy in the moment before its effect ends the run.
      for (const track of stream.getTracks()) track.stop();
      setStarting(false);
      return;
    }
    stopCamera();               // ends the start's run and puts starting back to false
    streamRef.current = stream;
    setPending(null);
    setScanState("scanning");
  }, [interactive, starting, stopCamera]);

  // A QR that is not a pass: say so, keep scanning, and let the line go
  // NOT_PASS_MS after the last miss. The same wrong code sits in front of the
  // camera for many frames; the state changes once, not per frame.
  const noteNotPass = () => {
    const np = notPassRef.current;
    if (np.timer) clearTimeout(np.timer);
    np.timer = setTimeout(() => {
      notPassRef.current = { shown: false, timer: null };
      if (mountedRef.current) setNotPass(false);
    }, NOT_PASS_MS);
    if (!np.shown) { np.shown = true; setNotPass(true); }
  };

  // What a decoded frame does. Held in a ref so the decode loop, started once
  // per camera run, always calls the current one.
  const onDecodedRef = useRef(null);
  onDecodedRef.current = async (text) => {
    const token = parsePersonalQr(text);
    if (!token) { noteNotPass(); return; }
    stopCamera();
    // The hash is this decode's run. Stop, Close, a busy rig or a new scan
    // before it lands ends the run and the card is not shown: the reader is
    // already where the greeter put it, with its own camera running.
    const run = scanRef.current.begin();
    const hash = await hashQrToken(token);
    if (!scanRef.current.isCurrent(run) || rigBusyRef.current) return;
    setPending({ token, hash, forLease: addModeLeaseRef.current });
    setScanState("identified");
  };

  // Once the viewfinder shows the <video>, attach the stream and run the decode
  // loop: a timer every SAMPLE_MS, one decode per tick, until stopCamera ends
  // the run. The decoder loads once per run.
  useEffect(() => {
    if (scanState !== "scanning") return undefined;
    const video = videoRef.current;
    const stream = streamRef.current;
    if (!video || !stream) { setScanState("idle"); return undefined; }
    const scan = scanRef.current;
    const run = scan.begin();
    video.muted = true;
    video.srcObject = stream;
    const played = video.play();
    if (played && typeof played.catch === "function") played.catch(() => { /* stopped before it played */ });

    let decoder = null;
    const giveUp = () => {
      if (!scan.isCurrent(run)) return;
      stopCamera();
      setCameraError(DECODER_ERROR_TEXT);
      setScanState("idle");
    };
    const tick = async () => {
      if (!scan.isCurrent(run)) return;
      if (decoder && video.readyState >= 2) {
        let text = null;
        try {
          text = await decoder.decode(video);
        } catch {
          // The native detector refused the frame: jsqr takes over for this run.
          if (decoder.native) {
            try { decoder = await makeJsqrDecoder(); } catch { giveUp(); return; }
          }
        }
        if (!scan.isCurrent(run)) return;
        if (text) await onDecodedRef.current(text);
        if (!scan.isCurrent(run)) return;
      }
      timerRef.current = setTimeout(tick, SAMPLE_MS);
    };
    (async () => {
      try { decoder = await makeDecoder(); } catch { giveUp(); return; }
      if (scan.isCurrent(run)) tick();
    })();
    return () => {
      if (scan.isCurrent(run)) scan.invalidate();
      if (timerRef.current) { clearTimeout(timerRef.current); timerRef.current = null; }
    };
  }, [scanState, stopCamera]);

  // The rig went busy (this reader's own arm, or the console's): camera off,
  // any confirm card dropped, back to idle behind the busy screen. A
  // permission prompt still up is dropped when it answers (stopCamera ended
  // its run and cleared starting), so the Tap to scan button does not wait on it.
  useEffect(() => {
    if (!rigBusy) return;
    stopCamera();
    setPending(null);
    setScanState("idle");
  }, [rigBusy, stopCamera]);

  useEffect(() => {
    mountedRef.current = true;
    return () => { mountedRef.current = false; stopCamera(); };
  }, [stopCamera]);

  // Group check-in. Add mode ends by itself when its arm leaves the rig or
  // closes (Start, cancel, another arm): camera off, card dropped, back to the
  // rig's screen. Without this a cancel (the rig back to idle) would leave the
  // camera running and turn a scan meant for a group into an arm on its own.
  const addingRef = useRef(adding);
  useEffect(() => {
    const was = addingRef.current;
    addingRef.current = adding;
    if (!was || adding) return;
    stopCamera();
    setPending(null);
    setScanState("idle");
    setAddLease(null);
  }, [adding, stopCamera]);

  // Opened from the console's "Scan a pass" (addFor: the job and lease of the
  // arm on that screen): straight into add mode for THAT arm, once, at the
  // first live state after the request, and only if the rig still shows that
  // arm open. Anything else (cancelled, another arm, Start pressed) drops the
  // request: the reader stays on the rig's screen and nobody else's capture
  // is joined by itself.
  const autoAddRef = useRef(addFor);
  useEffect(() => { autoAddRef.current = addFor; }, [addFor]);
  const stateKnown = stateProblem === null;
  useEffect(() => {
    const want = autoAddRef.current;
    if (!want || !stateKnown) return; // wait for the live state (a failed subscription never gets there)
    autoAddRef.current = null;
    const lease = addModeFor(want, { state, jobId, interactive });
    if (!lease) return;
    setError(null);
    setAddLease(lease);
  }, [state, stateKnown, interactive, addFor, jobId]);

  const people = groupPeopleOf(state);
  const maxSize = maxGroupSizeOf(job);
  const canAddMore = open && interactive && people.length < maxSize;
  const leadName = state?.subjectName || "";
  const handleAddMode = () => { setError(null); setAddLease(state?.leaseId ?? null); };
  const handleAddBack = () => { stopCamera(); setPending(null); setScanState("idle"); setAddLease(null); };
  const handleRemove = async (person) => {
    if (!open || !interactive || removingId) return;
    setRemovingId(person.rosterEntryId); setError(null);
    try {
      await rigv2.removeGroupMember({ jobId: job?.id ?? jobId, leaseId: state.leaseId, deviceId: getDeviceId(), rosterEntryId: person.rosterEntryId });
    } catch (err) { if (mountedRef.current) setError(err); }
    if (mountedRef.current) setRemovingId(null);
  };

  const handleSend = async () => {
    if (!cardPending || !interactive || sending || rigBusy) return;
    const pass = cardPending;
    const name = identified?.name || identified?.displayId || "";
    if (pass.forLease) {
      // Group check-in: the pass joins the capture it was scanned for (the
      // lease the reader was adding to), and the reader goes back to the
      // rig's screen, where the group now lists them. Someone already
      // captured joins only as a retake, asked first when the roster on this
      // phone shows it, else when the server says so (addWithRetakeCheck);
      // "No" adds nobody and keeps the card. A failure that is not a clear
      // refusal (a timeout) may still have added them: the words say to
      // check the list.
      setSending(true); setError(null);
      try {
        const outcome = await addWithRetakeCheck({
          add: (retake) => rigv2.addGroupMember({ jobId: job?.id ?? jobId, leaseId: pass.forLease, deviceId: getDeviceId(), qrToken: pass.token, retake }),
          knownCaptured: identified?.captured === true,
          confirm: (question) => window.confirm(question),
          name,
        });
        if (outcome === "added" && mountedRef.current) { setPending(null); setScanState("idle"); setAddLease(null); }
      } catch (err) { if (mountedRef.current) setError(new Error(groupAddErrorText(err, name))); }
      if (mountedRef.current) setSending(false);
      return;
    }
    setSending(true); setError(null);
    try {
      // The live arm path, by token: the same callable as the console's arm by
      // id. The server hashes the token and finds the entry, so a pass the
      // roster subscription has not shown yet still arms. A busy rig (this
      // phone's view of it was old) is refused by the server in plain words.
      const res = await ingest.arm({
        jobId: job?.id ?? jobId, sessionId: sessionId ?? null,
        qrToken: pass.token, deviceId: getDeviceId(), clientLeaseId: newLeaseId(),
      });
      const entry = identified ?? res?.rosterEntry ?? null;
      setHistory((h) => [{ id: entry?.id ?? "pass", name: entry?.name || entry?.displayId || "Pass", ts: Date.now() }, ...h.slice(0, 4)]);
      setPending(null);
      setScanState("idle");
    } catch (err) { if (mountedRef.current) setError(new Error(sendErrorText(err, name))); }
    if (mountedRef.current) setSending(false);
  };

  // "Wrong person, scan again": drop the card and reopen the camera on this
  // tap. stopCamera, not a bare invalidate: the rescan ends the old run the
  // same way Stop does, so nothing it left behind (a start still out, the
  // not-a-pass timer) carries into the scan that starts here.
  const handleReject = () => {
    if (sending) return;
    stopCamera();
    setPending(null);
    setScanState("idle");
    startCamera();
  };

  const handleStop = () => { stopCamera(); setScanState("idle"); };
  const handleClose = () => { stopCamera(); if (onClose) onClose(); };

  // Focus stays in the reader across its screens (a button that goes away,
  // like Send to rig, Add a person to this capture or Tap to scan, would
  // otherwise drop focus to the page behind it): the dialog around the reader
  // does that for the whole reader (QrReaderDialog in RigV2App, useModalDialog
  // in dialogFocus.js), so the reader has no focus handling of its own.

  // Escape closes the reader the way its Close button does (camera off first).
  // Read through a ref: the listener is added once, the handler is this render's.
  const escapeCloseRef = useRef(null);
  escapeCloseRef.current = onClose ? handleClose : null;
  useEffect(() => {
    const onKey = (e) => { if (e.key === "Escape" && escapeCloseRef.current) escapeCloseRef.current(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  return (
    <div className="rigv2-scanner-stage">
      <div className="rigv2-scanner-topline">
        <span>QR Reader, a handheld scanner scoped to this job. The rig reacts to what you send.</span>
        {onClose && <button type="button" className="rigv2-exit" onClick={handleClose}>Close</button>}
      </div>
      <div className="rigv2-device">
        <div className="rigv2-device-screen">
          <ScannerHeader
            jobName={job?.displayName ?? job?.clientName ?? jobId}
            rigBusy={rigBusy}
            onClose={onClose ? handleClose : null}
          />
          {/* Whose account the phone is using (docs/ops/password-signin.md);
              Sign out turns the camera off first. */}
          {signedInAs && (
            <div className="rigv2-scanner-whoami">
              <span className="rigv2-whoami-text">Signed in as <strong>{signedInAs}</strong></span>
              {onSignOut && (
                <button type="button" className="rigv2-whoami-signout" onClick={() => { stopCamera(); onSignOut(); }}>Sign out</button>
              )}
            </div>
          )}
          <div className="rigv2-scanner-body">
            <ScannerBody
              rigBusy={rigBusy} stateProblem={stateProblem} phase={phase} state={state}
              scanState={scanState} identified={identified} pending={cardPending}
              sending={sending} starting={starting} interactive={interactive}
              cameraError={cameraError} notPass={notPass} videoRef={videoRef}
              onScan={startCamera} onStop={handleStop} onSend={handleSend} onReject={handleReject}
              group={phase === "armed" ? { people, open, removingId } : null}
              onAddToGroup={canAddMore ? handleAddMode : null}
              onRemoveFromGroup={open && interactive ? handleRemove : null}
              groupAdd={adding ? { leadName, count: people.length, onBack: handleAddBack } : null}
            />
            {rosterError && <div className="rigv2-banner error">The list did not load: {rosterError.message ?? String(rosterError)}</div>}
            {error && <div className="rigv2-banner error">{error.message ?? String(error)}</div>}
            {!rigBusy && !adding && scanState === "idle" && history.length > 0 && (
              <div className="rigv2-scanner-recent">
                <div className="rigv2-scanner-recent-title">Sent to rig (last {history.length})</div>
                {history.map((h) => (
                  <div key={`${h.id}_${h.ts}`} className="rigv2-scanner-recent-row">
                    <span className="rigv2-roster-av">{initials(h.name)}</span>
                    <span className="rigv2-roster-name">{h.name}</span>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

/**
 * The reader's header (item 13). At desktop widths it is the line it has
 * always been: the rig dot, the job name, and "QR Reader, signed in" pushed to
 * the right, with Close living above the device on the white topline. On a
 * phone the topline is hidden and this header is the whole chrome the sheet
 * has, so it also carries Close as a 44 px tall ghost button at the right, and
 * ", signed in" drops away to leave "<job> QR Reader". Both Close buttons are
 * in the markup and each breakpoint hides one, so the desktop reader is
 * untouched. Pure: exported for the SSR test.
 */
export function ScannerHeader({ jobName, rigBusy = false, onClose = null }) {
  return (
    <div className="rigv2-scanner-header">
      <span className="rigv2-corner-dot" style={rigBusy ? { background: "var(--capture)" } : undefined} />
      <span className="rigv2-scanner-job">{jobName}</span>
      <span className="rigv2-scanner-meta">
        <span className="rigv2-scanner-what">QR Reader</span>
        <span className="rigv2-scanner-signed">, signed in</span>
      </span>
      {onClose && <button type="button" className="rigv2-scanner-close" onClick={onClose}>Close</button>}
    </div>
  );
}

// The viewfinder box: the live camera while the reader is scanning, a dashed
// placeholder otherwise. The label sits over the picture.
function Viewfinder({ live = false, videoRef = null, label }) {
  return (
    <div className={`rigv2-viewfinder${live ? " live" : ""}`}>
      {live && <video ref={videoRef} className="rigv2-viewfinder-video" playsInline muted autoPlay />}
      <span className="rigv2-viewfinder-label">{label}</span>
    </div>
  );
}

/**
 * What the idle screen shows, which is the one thing a start still out
 * changes there. While it is out (the permission prompt, which on a phone can
 * sit there until someone answers it) the viewfinder says the camera is
 * starting and the button is an enabled Stop: it ends the scan run, so the
 * stream that arrives after it is stopped instead of attached and the next
 * Tap to scan is not refused. Otherwise it is Tap to scan, off only on a
 * reader that is not interactive; Stop is never off, because a reader that is
 * not interactive never starts a camera in the first place. A busy rig is
 * answered before this, with its own button. Plain JS, exported for the test.
 */
export function idleScreen({ starting = false, interactive = true } = {}) {
  if (starting) {
    return {
      viewfinder: "Starting camera…", line: STARTING_LINE,
      button: { label: "Stop", kind: "ghost", disabled: false, stop: true },
    };
  }
  return {
    viewfinder: "Camera off", line: IDLE_LINE,
    button: { label: "Tap to scan", kind: "primary", disabled: !interactive, stop: false },
  };
}

// Pure presentational scanner body, keyed on rig-busy and the scan state.
// Exported for deterministic SSR tests (the live useCaptureState, the roster
// subscription and the camera run in the container above).
export function ScannerBody({
  rigBusy, stateProblem = null, phase, state, scanState, identified, pending = null, sending = false, starting = false,
  interactive, cameraError = null, notPass = false, videoRef = null, onScan, onStop, onSend, onReject,
  group = null, onAddToGroup = null, onRemoveFromGroup = null, groupAdd = null,
}) {
  // The reader does not know whether the rig is free (its live state has not
  // arrived, or the subscription failed): nothing to scan or send until it does.
  if (stateProblem) {
    const failed = stateProblem.kind === "error";
    return (
      <>
        <div className={`rigv2-banner ${failed ? "error" : "warn"}`} role={failed ? "alert" : "status"}>
          <strong>{failed ? "The reader lost the rig's status" : "Checking the rig…"}</strong>
          <div className="rigv2-scanner-wait">
            {failed ?
              `It cannot tell whether the rig is free (${stateProblem.message}), so it will not scan or send anyone. Close the reader and open it again.` :
              "Scanning starts once the reader knows the rig is free."}
          </div>
        </div>
        <button type="button" className="rigv2-btn primary rigv2-scanner-cta" disabled>Tap to scan</button>
      </>
    );
  }
  if (rigBusy) {
    const label = { armed: "Rig is loaded", counting_down: "Countdown in progress", capturing: "Capture in progress" }[phase] ?? "Rig is busy";
    // Group check-in: the group on an armed rig, and the way to add to it.
    const people = group?.people ?? [];
    return (
      <>
        <div className="rigv2-banner warn">
          <strong>{label}</strong>
          {state?.subjectName && <div>Subject: {state.subjectName}</div>}
          <div className="rigv2-scanner-wait">Waiting for the rig to return to idle before the next scan.</div>
        </div>
        {people.length > 1 && (
          <div className="rigv2-group-box">
            <div className="rigv2-group-count">{groupCountLabel(people.length)}</div>
            <GroupList people={people} onRemove={onRemoveFromGroup} removingId={group?.removingId ?? null} disabled={!group?.open || !interactive} />
          </div>
        )}
        {onAddToGroup && (
          <button type="button" className="rigv2-btn rigv2-scanner-cta" onClick={onAddToGroup}>Add a person to this capture</button>
        )}
        {group && !group.open && people.length > 1 && <p className="rigv2-scanner-note">Start was pressed, so the group is locked.</p>}
        <button type="button" className="rigv2-btn primary rigv2-scanner-cta" disabled>Tap to scan</button>
      </>
    );
  }
  // Group check-in, add mode: the same screens, about joining the capture on
  // the rig instead of sending someone to it.
  const lead = groupAdd ? (groupFirstName(groupAdd.leadName) || "the first person") : "";
  const intoCapture = groupAdd ? `Add to ${lead}'s capture` : null;
  const backButton = groupAdd ? (
    <button type="button" className="rigv2-btn ghost rigv2-scanner-cta" disabled={sending} onClick={groupAdd.onBack}>Back to the rig</button>
  ) : null;
  if (scanState === "identified" && identified && groupAdd) {
    const first = firstName(identified.name);
    const guardian = identified.personFields?.isMinor ? (identified.personFields.guardianName || "the guardian") : null;
    const problem = groupJoinProblem(identified, state);
    const meta = [
      identified.displayId ? `No. ${identified.displayId}` : null,
      maskEmail(identified.personFields?.email),
      identified.captured ? "captured" : null,
    ].filter(Boolean).join(" · ");
    return (
      <>
        <h2 className="rigv2-heading">Add this person to the capture?</h2>
        <p className="rigv2-sub">They join {lead}'s capture: one capture for everyone in it ({groupCountLabel(groupAdd.count + 1)} with them).</p>
        <div className="rigv2-scanner-identified">
          <span className="rigv2-roster-av">{initials(identified.name || identified.displayId)}</span>
          <span className="rigv2-scanner-idbody">
            <span className="rigv2-roster-name">{identified.name || identified.displayId || "Unnamed"}</span>
            {meta && <span className="rigv2-roster-meta">{meta}</span>}
          </span>
          <RosterBadges entry={identified} className="rigv2-roster-badges rigv2-scanner-badges" />
        </div>
        {problem ? (
          <p className="rigv2-scanner-alert" role="alert">{groupJoinProblemText(problem, identified.name || identified.displayId)}</p>
        ) : (
          <p className="rigv2-scanner-ask">
            {first ? `Ask: are you ${first}?` : "Ask: is this their pass?"}{guardian ? ` Is ${guardian} with you?` : ""}
          </p>
        )}
        {!problem && identified.captured && <p className="rigv2-scanner-note">Already captured: adding them makes a retake.</p>}
        <button type="button" className="rigv2-btn primary rigv2-scanner-cta" disabled={!interactive || sending || !!problem} onClick={onSend}>
          {sending ? "Adding…" : intoCapture}
        </button>
        <button type="button" className="rigv2-btn ghost rigv2-scanner-cta" disabled={sending} onClick={onReject}>Wrong person, scan again</button>
        {backButton}
      </>
    );
  }
  if (scanState === "identified" && pending && groupAdd) {
    return (
      <>
        <h2 className="rigv2-heading">Pass scanned</h2>
        <p className="rigv2-sub">They may have registered a moment ago. The rig looks the pass up when you add them.</p>
        <p className="rigv2-scanner-ask">Not in the list yet. {intoCapture} anyway?</p>
        <button type="button" className="rigv2-btn primary rigv2-scanner-cta" disabled={!interactive || sending} onClick={onSend}>
          {sending ? "Adding…" : intoCapture}
        </button>
        <button type="button" className="rigv2-btn ghost rigv2-scanner-cta" disabled={sending} onClick={onReject}>Scan again</button>
        {backButton}
      </>
    );
  }
  if (scanState === "identified" && identified) {
    const first = firstName(identified.name);
    const guardian = identified.personFields?.isMinor ? (identified.personFields.guardianName || "the guardian") : null;
    // The address is masked: the phone faces the queue.
    const meta = [
      identified.displayId ? `No. ${identified.displayId}` : null,
      maskEmail(identified.personFields?.email),
      identified.captured ? "captured" : null,
    ].filter(Boolean).join(" · ");
    return (
      <>
        <h2 className="rigv2-heading">Is this the right person?</h2>
        <p className="rigv2-sub">Check the name before sending them to the rig.</p>
        <div className="rigv2-scanner-identified">
          <span className="rigv2-roster-av">{initials(identified.name || identified.displayId)}</span>
          <span className="rigv2-scanner-idbody">
            <span className="rigv2-roster-name">{identified.name || identified.displayId || "Unnamed"}</span>
            {meta && <span className="rigv2-roster-meta">{meta}</span>}
          </span>
          <RosterBadges entry={identified} className="rigv2-roster-badges rigv2-scanner-badges" />
        </div>
        <p className="rigv2-scanner-ask">
          {first ? `Ask: are you ${first}?` : "Ask: is this their pass?"}{guardian ? ` Is ${guardian} with you?` : ""}
        </p>
        <button type="button" className="rigv2-btn primary rigv2-scanner-cta" disabled={!interactive || sending} onClick={onSend}>
          {sending ? "Sending…" : "Send to rig"}
        </button>
        <button type="button" className="rigv2-btn ghost rigv2-scanner-cta" disabled={sending} onClick={onReject}>Wrong person, scan again</button>
      </>
    );
  }
  if (scanState === "identified" && pending) {
    return (
      <>
        <h2 className="rigv2-heading">Pass scanned</h2>
        <p className="rigv2-sub">They may have registered a moment ago. The rig looks the pass up on Send.</p>
        <p className="rigv2-scanner-ask">{NOT_IN_LIST_TEXT}</p>
        <button type="button" className="rigv2-btn primary rigv2-scanner-cta" disabled={!interactive || sending} onClick={onSend}>
          {sending ? "Sending…" : "Send to rig"}
        </button>
        <button type="button" className="rigv2-btn ghost rigv2-scanner-cta" disabled={sending} onClick={onReject}>Scan again</button>
      </>
    );
  }
  if (scanState === "scanning") {
    return (
      <>
        <Viewfinder live videoRef={videoRef} label="Point at the pass" />
        <p className="rigv2-sub">{groupAdd ? `Adding to ${lead}'s capture. Point at the next person's pass.` : IDLE_LINE}</p>
        {notPass && <p className="rigv2-scanner-alert" role="status">{NOT_PASS_TEXT}</p>}
        <p className="rigv2-scanner-note">{NOTE_LINE}</p>
        <button type="button" className="rigv2-btn ghost rigv2-scanner-cta" onClick={onStop}>Stop camera</button>
      </>
    );
  }
  // idle: the camera is off until the tap, and starting from the tap until the
  // stream, where the screen offers Stop instead (idleScreen).
  const screen = idleScreen({ starting, interactive });
  return (
    <>
      <Viewfinder label={screen.viewfinder} />
      <h2 className="rigv2-heading">{groupAdd ? "Add a person" : "Ready to scan"}</h2>
      <p className="rigv2-sub">{groupAdd && !starting ? `Scan the pass of the next person joining ${lead}'s capture.` : screen.line}</p>
      {cameraError && <div className="rigv2-banner error" role="alert">{cameraError}</div>}
      <p className="rigv2-scanner-note">{NOTE_LINE}</p>
      <button type="button" className={`rigv2-btn ${screen.button.kind} rigv2-scanner-cta`}
        disabled={screen.button.disabled} onClick={screen.button.stop ? onStop : onScan}>
        {screen.button.label}
      </button>
      {backButton}
    </>
  );
}
